import type {
  AccountFilterParams,
  AccountingReportFilterParams,
  JournalEntryFilterParams,
} from '@/renderer/types/accounting';
import type {
  InventoryDashboardFilterParams,
  InventoryItemFilterParams,
  InventoryRecipeFilterParams,
  InventoryStockBalanceFilterParams,
  InventoryStockLocationFilterParams,
  InventoryStockMovementFilterParams,
  InventoryStockMovementItemOptionFilterParams,
  InventorySupplierFilterParams,
  InventorySupplierInvoiceFilterParams,
  InventorySupplierPaymentFilterParams,
  StockAdjustmentFilterParams,
  StockTransferFilterParams,
} from '@/renderer/types/inventory';

export const queryKeys = {
  accommodation: {
    all: ['accommodation'] as const,
    bookingSources: () => ['accommodation', 'booking-sources'] as const,
    countries: () => ['accommodation', 'countries'] as const,

    roomTypes: (propertyId: string) =>
      ['accommodation', 'room-types', propertyId] as const,

    rooms: (propertyId: string) =>
      ['accommodation', 'rooms', propertyId] as const,

    ratePlans: (propertyId: string) =>
      ['accommodation', 'rate-plans', propertyId] as const,

    bookings: (propertyId: string, filters?: object) =>
      ['accommodation', 'bookings', propertyId, filters ?? null] as const,

    bookingEvents: (bookingId: string) =>
      ['accommodation', 'booking-events', bookingId] as const,

    bookingPayments: (bookingId: string) =>
      ['accommodation', 'booking-payments', bookingId] as const,

    propertyPayments: (propertyId: string, filters?: object) =>
      [
        'accommodation',
        'property-payments',
        propertyId,
        filters ?? null,
      ] as const,

    dashboardStats: (propertyId: string, dateFrom: string, dateTo: string) =>
      [
        'accommodation',
        'dashboard-stats',
        propertyId,
        dateFrom,
        dateTo,
      ] as const,

    guests: (outletId: string | null | undefined) =>
      ['accommodation', 'guests', outletId ?? null] as const,

    guestDetails: (guestId: string, outletId: string | null | undefined) =>
      ['accommodation', 'guest-details', guestId, outletId ?? null] as const,

    clientDetails: (clientId: string | null | undefined) =>
      ['accommodation', 'client-details', clientId ?? null] as const,

    revPar: (propertyId: string, dateFrom: string, dateTo: string) =>
      ['accommodation', 'rev-par', propertyId, dateFrom, dateTo] as const,

    outstandingOrders: (clientId: string) =>
      ['accommodation', 'outstanding-orders', clientId] as const,
  },

  menu: {
    all: ['menu'] as const,
    departments: (outletId?: string | null) =>
      ['menu', 'departments', outletId ?? null] as const,
    categories: (outletId?: string | null) =>
      ['menu', 'categories', outletId ?? null] as const,
    menuItems: (outletId?: string | null, searchQuery?: string) =>
      ['menu', 'items', outletId ?? null, searchQuery ?? ''] as const,
    menuItemsList: (params: object) => ['menu', 'items-list', params] as const,
    missingRecipes: (params: object) =>
      ['menu', 'missing-recipes', params] as const,
    missingFiscalSetup: (params: object) =>
      ['menu', 'missing-fiscal-setup', params] as const,
    configuredFiscalSetup: (params: object) =>
      ['menu', 'configured-fiscal-setup', params] as const,
    itemClassifications: (params: object) =>
      ['menu', 'item-classifications', params] as const,
    menuCounts: (outletId?: string | null) =>
      ['menu', 'counts', outletId ?? null] as const,
    addons: (outletId?: string | null) =>
      ['menu', 'addons', outletId ?? null] as const,
  },

  accounting: {
    all: ['accounting'] as const,

    dashboard: (
      companyId: number,
      outletId: string | null | undefined,
      dateFrom: string,
      dateTo: string,
    ) =>
      [
        'accounting',
        'dashboard',
        companyId,
        outletId ?? null,
        dateFrom,
        dateTo,
      ] as const,

    accountsList: (params: AccountFilterParams) =>
      ['accounting', 'accounts-list', params] as const,

    accountSummary: (companyId: number, search: string) =>
      ['accounting', 'account-summary', companyId, search] as const,

    accountOptions: (companyId: number) =>
      ['accounting', 'account-options', companyId] as const,

    journalsList: (params: JournalEntryFilterParams) =>
      ['accounting', 'journals-list', params] as const,

    journalDetail: (companyId: number, entryId: string) =>
      ['accounting', 'journal-detail', companyId, entryId] as const,

    trialBalance: (
      companyId: number,
      outletId: string | null | undefined,
      search: string,
    ) =>
      [
        'accounting',
        'trial-balance',
        companyId,
        outletId ?? null,
        search,
      ] as const,

    generalLedger: (params: AccountingReportFilterParams) =>
      ['accounting', 'general-ledger', params] as const,

    accountActivity: (params: AccountingReportFilterParams) =>
      ['accounting', 'account-activity', params] as const,
  },

  inventory: {
    all: ['inventory'] as const,

    dashboard: (params: InventoryDashboardFilterParams) =>
      ['inventory', 'dashboard', params] as const,

    itemsList: (params: InventoryItemFilterParams) =>
      ['inventory', 'items-list', params] as const,

    categories: (companyId: number) =>
      ['inventory', 'categories', companyId] as const,

    units: (companyId: number) => ['inventory', 'units', companyId] as const,

    itemSummary: (
      companyId: number,
      search: string,
      categoryId: string,
      status: string,
    ) =>
      [
        'inventory',
        'item-summary',
        companyId,
        search,
        categoryId,
        status,
      ] as const,

    suppliersList: (params: InventorySupplierFilterParams) =>
      ['inventory', 'suppliers-list', params] as const,

    supplierSummary: (companyId: number, search: string, status: string) =>
      ['inventory', 'supplier-summary', companyId, search, status] as const,

    stockLocationsList: (params: InventoryStockLocationFilterParams) =>
      ['inventory', 'stock-locations-list', params] as const,

    stockLocationSummary: (
      companyId: number,
      outletId: string,
      search: string,
      type: string,
      status: string,
    ) =>
      [
        'inventory',
        'stock-location-summary',
        companyId,
        outletId,
        search,
        type,
        status,
      ] as const,

    stockBalancesList: (params: InventoryStockBalanceFilterParams) =>
      ['inventory', 'stock-balances-list', params] as const,

    stockBalanceSummary: (
      companyId: number,
      outletId: string,
      search: string,
      stockLocationId: string,
      inventoryItemIds: string[],
    ) =>
      [
        'inventory',
        'stock-balance-summary',
        companyId,
        outletId,
        search,
        stockLocationId,
        inventoryItemIds,
      ] as const,

    stockMovementsList: (params: InventoryStockMovementFilterParams) =>
      ['inventory', 'stock-movements-list', params] as const,

    stockMovementItemOptions: (
      params: InventoryStockMovementItemOptionFilterParams,
    ) => ['inventory', 'stock-movement-item-options', params] as const,

    stockMovementSummary: (
      companyId: number,
      outletId: string,
      search: string,
      stockLocationId: string,
      movementType: string,
      inventoryItemIds: string[],
      dateFrom: string,
      dateTo: string,
    ) =>
      [
        'inventory',
        'stock-movement-summary',
        companyId,
        outletId,
        search,
        stockLocationId,
        movementType,
        inventoryItemIds,
        dateFrom,
        dateTo,
      ] as const,

    stockMovementItemSummary: (
      params: Omit<InventoryStockMovementFilterParams, 'page' | 'pageSize'>,
    ) => ['inventory', 'stock-movement-item-summary', params] as const,

    supplierInvoicesList: (params: InventorySupplierInvoiceFilterParams) =>
      ['inventory', 'supplier-invoices-list', params] as const,

    supplierInvoiceDetail: (companyId: number, invoiceId: string) =>
      ['inventory', 'supplier-invoice-detail', companyId, invoiceId] as const,

    supplierPaymentsList: (params: InventorySupplierPaymentFilterParams) =>
      ['inventory', 'supplier-payments-list', params] as const,

    supplierInvoiceSummary: (
      companyId: number,
      outletId: string,
      supplierId: string,
      status: string,
      dateFrom: string,
      dateTo: string,
      inventoryItemIds: string[],
    ) =>
      [
        'inventory',
        'supplier-invoice-summary',
        companyId,
        outletId,
        supplierId,
        status,
        dateFrom,
        dateTo,
        inventoryItemIds,
      ] as const,

    recipesList: (params: InventoryRecipeFilterParams) =>
      ['inventory', 'recipes-list', params] as const,

    recipeDetail: (companyId: number, recipeId: string) =>
      ['inventory', 'recipe-detail', companyId, recipeId] as const,

    recipeSummary: (companyId: number, outletId: string, status: string) =>
      ['inventory', 'recipe-summary', companyId, outletId, status] as const,

    stockTransfersList: (params: StockTransferFilterParams) =>
      ['inventory', 'stock-transfers-list', params] as const,

    stockTransferDetail: (companyId: number, transferId: string) =>
      ['inventory', 'stock-transfer-detail', companyId, transferId] as const,

    stockTransferSummary: (companyId: number, outletId: string) =>
      ['inventory', 'stock-transfer-summary', companyId, outletId] as const,

    stockAdjustmentsList: (params: StockAdjustmentFilterParams) =>
      ['inventory', 'stock-adjustments-list', params] as const,

    stockAdjustmentDetail: (companyId: number, adjustmentId: string) =>
      [
        'inventory',
        'stock-adjustment-detail',
        companyId,
        adjustmentId,
      ] as const,

    stockAdjustmentSummary: (companyId: number, outletId: string) =>
      ['inventory', 'stock-adjustment-summary', companyId, outletId] as const,

    lowStockItemsList: (params: object) =>
      ['inventory', 'low-stock-items', params] as const,
  },
  clients: {
    all: ['clients'] as const,
    list: (params: object) => ['clients', 'list', params] as const,
    byId: (clientId: string | null | undefined) =>
      ['clients', 'by-id', clientId ?? null] as const,
  },
  devices: {
    all: ['devices'] as const,
    list: (outletId?: string | null) =>
      ['devices', 'list', outletId ?? null] as const,
  },
  users: {
    all: ['users'] as const,
    list: (outletId?: string | null) =>
      ['users', 'list', outletId ?? null] as const,
    paginatedList: (params: object) =>
      ['users', 'paginated-list', params] as const,
    metrics: (outletId?: string | null) =>
      ['users', 'metrics', outletId ?? null] as const,
  },
  ebm: {
    all: ['ebm'] as const,
    saleRecords: (params: object) => ['ebm', 'sale-records', params] as const,
  },
  settingsDashboard: {
    all: ['settings-dashboard'] as const,
    source: (source: string, scope: object) =>
      ['settings-dashboard', source, scope] as const,
  },
};
